import { useCallback, useEffect, useRef, useState } from "react"
import styles from "../../styles/components/blogs/CategoryFilter.module.css"

/**
 * Category Filter Component with professional horizontal scrolling
 * @param {Object} props
 * @param {Array} props.categories - Array of category names
 * @param {string} props.selectedCategory - Currently selected category
 * @param {function} props.onCategorySelect - Callback when category is selected
 */
export default function CategoryFilter({ categories, selectedCategory, onCategorySelect }) {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)
  const containerRef = useRef(null)
  const scrollTimeoutRef = useRef(null)

  // Check scrollability of categories container
  const checkScrollability = useCallback(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const { scrollLeft, scrollWidth, clientWidth } = container
    
    const hasOverflow = scrollWidth > clientWidth
    const canScrollLeftValue = scrollLeft > 5
    const canScrollRightValue = scrollLeft < scrollWidth - clientWidth - 5

    setIsScrollable(hasOverflow)
    setCanScrollLeft(canScrollLeftValue)
    setCanScrollRight(canScrollRightValue)
  }, [])

  // Scroll to specific position
  const scrollTo = useCallback((scrollLeft) => {
    if (!containerRef.current) return

    containerRef.current.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    })
  }, [])

  // Scroll left
  const scrollLeft = useCallback(() => {
    if (!containerRef.current) return

    const scrollAmount = containerRef.current.clientWidth * 0.8
    const newScrollLeft = Math.max(0, containerRef.current.scrollLeft - scrollAmount)
    scrollTo(newScrollLeft)
  }, [scrollTo])

  // Scroll right
  const scrollRight = useCallback(() => {
    if (!containerRef.current) return

    const scrollAmount = containerRef.current.clientWidth * 0.8
    const maxScroll = containerRef.current.scrollWidth - containerRef.current.clientWidth
    const newScrollLeft = Math.min(maxScroll, containerRef.current.scrollLeft + scrollAmount)
    scrollTo(newScrollLeft)
  }, [scrollTo])

  // Scroll to selected category
  const scrollToSelectedCategory = useCallback(() => {
    if (!containerRef.current || !selectedCategory) return

    const selectedButton = containerRef.current.querySelector(`[data-category="${selectedCategory}"]`)
    if (!selectedButton) return

    const container = containerRef.current
    const containerRect = container.getBoundingClientRect()
    const buttonRect = selectedButton.getBoundingClientRect()
    
    const scrollLeft = buttonRect.left - containerRect.left + container.scrollLeft - (containerRect.width / 2) + (buttonRect.width / 2)
    
    scrollTo(scrollLeft)
  }, [selectedCategory, scrollTo])

  // Handle scroll with debouncing
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      checkScrollability()
    }, 10)
  }, [checkScrollability])

  // Initialize and update on changes
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollability()
      scrollToSelectedCategory()
    }, 100)

    return () => clearTimeout(timer)
  }, [checkScrollability, scrollToSelectedCategory, categories, selectedCategory])

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      checkScrollability()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [checkScrollability])

  return (
    <div className={styles.categoryFilterWrapper}>
      {/* Left scroll button */}
      {isScrollable && canScrollLeft && (
        <button
          className={`${styles.scrollButton} ${styles.scrollLeft}`}
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>
      )}

      {/* Categories container */}
      <div 
        ref={containerRef}
        className={`${styles.categoriesContainer} ${canScrollLeft ? styles['scrollable-left'] : ''} ${canScrollRight ? styles['scrollable-right'] : ''}`}
        onScroll={handleScroll}
      >
        <div className={styles.categoriesWrapper}>
          {categories.map((category) => (
            <button
              key={category}
              data-category={category}
              className={`${styles.categoryBtn} ${selectedCategory === category ? styles.active : ""}`}
              onClick={() => onCategorySelect(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Right scroll button */}
      {isScrollable && canScrollRight && (
        <button
          className={`${styles.scrollButton} ${styles.scrollRight}`}
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>
      )}
    </div>
  )
}
